import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Pin,
  FileText,
  Download,
  Pencil,
  Archive,
  Trash2,
} from 'lucide-react';
import {
  useAnnouncement,
  useDeleteAnnouncement,
  useUpdateAnnouncement,
  useAnnouncementAttachmentUrl,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';

const AUDIENCE_LABELS: Record<string, string> = {
  ALL: 'Todos',
  BY_COUNTRY: 'Por país',
  BY_DEPARTMENT: 'Por departamento',
  BY_COUNTRY_AND_DEPARTMENT: 'País y depto.',
};

export function AnnouncementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: a, isLoading } = useAnnouncement(id);
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'ADMIN' || role === 'HR';
  const del = useDeleteAnnouncement();
  const update = useUpdateAnnouncement();
  const getAttachmentUrl = useAnnouncementAttachmentUrl();

  const handleDelete = async () => {
    if (!id || !confirm('¿Eliminar este anuncio permanentemente?')) return;
    try {
      await del.mutateAsync(id);
      navigate('/anuncios');
    } catch (err: any) {
      alert(err?.response?.data?.error?.message ?? 'No se pudo eliminar');
    }
  };

  const handleArchive = async () => {
    if (!id || !a) return;
    const newStatus = a.status === 'ARCHIVED' ? 'PUBLISHED' : 'ARCHIVED';
    try {
      await update.mutateAsync({ id, payload: { status: newStatus } });
    } catch (err: any) {
      alert(err?.response?.data?.error?.message ?? 'Error');
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    try {
      const { url } = await getAttachmentUrl.mutateAsync(id);
      window.open(url, '_blank');
    } catch {
      alert('No se pudo obtener el adjunto');
    }
  };

  if (isLoading) {
    return <p className="py-10 text-center text-font-secondary">Cargando...</p>;
  }
  if (!a) {
    return (
      <p className="py-10 text-center text-font-secondary">
        Anuncio no encontrado.
      </p>
    );
  }

  const authorName = a.createdBy?.employee
    ? `${a.createdBy.employee.firstName} ${a.createdBy.employee.lastName}`
    : a.createdBy?.email ?? '–';
  const authorRole = a.createdBy?.employee?.position?.title ?? '';
  const authorPhoto = a.createdBy?.employee?.photoUrl;

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/anuncios"
        className="inline-flex items-center gap-2 text-sm font-medium text-font-secondary hover:text-primary"
      >
        <ArrowLeft size={16} /> Volver a anuncios
      </Link>

      <div className="rounded-lg border border-border bg-white p-8 shadow-sm">
        {/* Title */}
        <h1 className="text-2xl font-bold text-font-primary">{a.title}</h1>

        {/* Meta */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {authorPhoto ? (
            <img
              src={authorPhoto}
              alt={authorName}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-light text-sm font-semibold text-white">
              {initials(authorName)}
            </div>
          )}
          <div>
            <p className="text-sm font-semibold text-font-primary">{authorName}</p>
            {authorRole && (
              <p className="text-xs text-font-secondary">{authorRole}</p>
            )}
          </div>
          <span className="text-xs text-font-tertiary">
            {a.publishedAt
              ? new Date(a.publishedAt).toLocaleDateString('es', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              : new Date(a.createdAt).toLocaleDateString('es', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
          </span>
          <span className="rounded-full bg-primary-subtle px-2 py-0.5 text-[10px] font-semibold text-primary">
            {AUDIENCE_LABELS[a.audienceType] ?? a.audienceType}
          </span>
          {a.pinned && (
            <span className="flex items-center gap-1 rounded-full bg-yellow-50 px-2 py-0.5 text-[10px] font-semibold text-warning">
              <Pin size={10} /> Fijado
            </span>
          )}
          {a.status === 'DRAFT' && (
            <span className="rounded-full bg-yellow-50 px-2 py-0.5 text-[10px] font-semibold text-warning">
              Borrador
            </span>
          )}
          {a.status === 'ARCHIVED' && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-font-secondary">
              Archivado
            </span>
          )}
        </div>

        {/* Divider */}
        <hr className="my-6 border-border" />

        {/* Body */}
        <div className="prose prose-sm max-w-none text-font-primary">
          {a.body.split('\n').map((line: string, i: number) => {
            if (!line.trim()) return <br key={i} />;
            if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
              return (
                <p key={i} className="ml-4 flex items-start gap-2 text-sm text-font-primary">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-font-primary" />
                  {line.trim().slice(2)}
                </p>
              );
            }
            return (
              <p key={i} className="text-sm leading-relaxed text-font-primary">
                {line}
              </p>
            );
          })}
        </div>

        {/* Attachment */}
        {a.attachmentName && (
          <div className="mt-6 flex items-center gap-4 rounded-lg border border-border bg-bg p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-red-50 text-error">
              <FileText size={18} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-font-primary">
                {a.attachmentName}
              </p>
              {a.attachmentSize && (
                <p className="text-xs text-font-tertiary">
                  {(a.attachmentSize / 1024).toFixed(0)} KB
                </p>
              )}
            </div>
            <button
              onClick={handleDownload}
              className="flex h-9 items-center gap-2 rounded-md border border-border bg-white px-4 text-xs font-semibold text-font-primary hover:bg-bg"
            >
              <Download size={14} /> Descargar
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 flex items-center justify-between border-t border-border pt-5">
          {isAdmin && (
            <p className="text-xs text-font-tertiary">
              Leído por {a.readCount ?? 0} de {a.totalAudience ?? '?'} colaboradores
            </p>
          )}
          {!isAdmin && <span />}

          {isAdmin && (
            <div className="flex items-center gap-2">
              <Link
                to={`/anuncios/${a.id}/editar`}
                className="flex h-9 items-center gap-2 rounded-md border border-border bg-white px-4 text-xs font-semibold text-font-primary hover:bg-bg"
              >
                <Pencil size={12} /> Editar
              </Link>
              <button
                onClick={handleArchive}
                disabled={update.isPending}
                className="flex h-9 items-center gap-2 rounded-md border border-border bg-white px-4 text-xs font-semibold text-font-primary hover:bg-bg disabled:opacity-60"
              >
                <Archive size={12} />{' '}
                {a.status === 'ARCHIVED' ? 'Republicar' : 'Archivar'}
              </button>
              <button
                onClick={handleDelete}
                disabled={del.isPending}
                className="flex h-9 items-center gap-2 rounded-md border border-error bg-white px-4 text-xs font-semibold text-error hover:bg-red-50 disabled:opacity-60"
              >
                <Trash2 size={12} /> Eliminar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
